import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { client, paypalCheckout, dataCollector } from 'braintree-web';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import Router from 'next/router';
import * as Sentry from '@sentry/node';

import Button from '../../atoms/button';

import { updateCart } from '../../store/modules/cart/actions';
import { createBraintreeTransaction } from '../../pages/api';
import { useScript } from '../../utils/hooks';
import trackEvent from '../../utils/tracking';

const PayPalWrapper = styled.div`
  position: relative;
  min-height: 4.5rem;
  overflow: hidden;
`;

const PayPalOriginal = styled.div`
  /*
   * Overlay button on top of custom button
   * https://github.com/paypal/paypal-checkout-components/issues/138
   * The thread is old, but we are using the paypal checkout.js API
   * and not the newer API...
   * :magikarp:
   */

  height: 100%;
  opacity: 0.1;
  position: absolute;
  transform: scale(10);
  z-index: ${({ theme }) => theme.layers.audience};
`;

const PPButtonSpace = styled.span`
  display: inline-block;
  position: relative;
  vertical-align: top;
  white-space: pre-wrap;
`;

const PayPal = styled(Button)`
  align-items: center;
  background: ${({ theme }) => theme.color.paypalYellow};
  border-color: ${({ theme }) => theme.color.paypalYellow};
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  height: 4.4rem;
  justify-content: center;
  outline: 0;
  overflow: hidden;
  position: relative;
  width: 100%;

  :hover {
    background-color: ${({ theme }) => theme.color.paypalYellowHover};
    opacity: 1;
    transition: background-color ${({ theme }) => theme.animation.default} ${({ theme }) => theme.animation.easeOutQuad};
  }

  > img {
    height: 2.2rem;
    max-height: 2.8rem,;
    min-height: 1.8rem;
  }
`;

const Loading = styled.span`
  bottom: 0;
  left: 0;
  opacity: 0.75;
  position: absolute;
  right: 0;
  top: 0;
  z-index: ${(props) => props.theme.layers.audience};

  ${(props) => props.theme.loader(3, props.theme.color.paypalYellow)}
`;

const LoadPaypal = (props) => {
  const [loaded, error] = useScript('https://www.paypalobjects.com/api/checkout.min.js', {
    'data-version-4': true
  });

  useEffect(() => {
    try {
      if (error) {
        const loadingError = new Error('PayPal failed to load');
        Sentry.captureException(loadingError);
        throw loadingError;
      }
    } catch (err) {
      console.error(err);
    }
  }, [error]);

  useEffect(() => {
    if (loaded) props.setPaypalLoaded(true);
  }, [loaded]);

  return null;
};

const BraintreePayPal = (props) => {
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payment] = useState(props.cart.payment_methods.find((m) => m.name === 'Braintree'));

  let clientInstance = null;
  let deviceData = null;

  useEffect(() => {
    if (!paypalLoaded) {
      return;
    }

    client.create({
      authorization: props.token
    }).then(async (instance) => {
      clientInstance = instance;

      return dataCollector.create({
        client: instance,
        kount: true,
        paypal: true
      });
    }).then((dataCollectorInstance) => {
      deviceData = dataCollectorInstance.deviceData;

      return paypalCheckout.create({
        client: clientInstance
      });
    }).then((paypalCheckoutInstance) => {
      if (Object.prototype.hasOwnProperty.call(global, 'paypal')) {
        return global.paypal.Button.render({
          env: process.env.NEXT_PUBLIC_PAYPAL_ENV,
          locale: 'en_US',
          style: {
            shape: 'rect',
            tagline: 'false'
          },

          /* enables 'Pay Now' checkout flow */
          commit: true,

          payment: () => paypalCheckoutInstance.createPayment({
            flow: 'vault',
            currency: 'USD',
            amount: props.cart.subtotals.order_total,
            // this value must either be `capture` or
            // match the intent passed into the PayPal
            // SDK intent query parameter
            intent: 'capture',
            enableShippingAddress: true
          }),

          onAuthorize(data) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Attempt'
            });

            return paypalCheckoutInstance.tokenizePayment(data)
              // some logic here before tokenization happens below
              .then(async (order) => {
                // Submit payload.nonce to your server
                const request = {
                  order_id: props.cart.number,
                  payment_method_id: payment.id,
                  state: 'payment',
                  options: {
                    restart_checkout: false
                  },
                  transaction: {
                    device_data: deviceData,
                    email: order.details.email,
                    nonce: order.nonce,
                    phone: order.details.phone,
                    payment_type: 'PayPalAccount',
                    shipping_address_attributes: {
                      first_name: order.details.firstName,
                      last_name: order.details.lastName,
                      address_line_1: order.details.shippingAddress.line1,
                      address_line_2: order.details.shippingAddress.line2,
                      city: order.details.shippingAddress.city,
                      state_code: order.details.shippingAddress.state,
                      zip: order.details.shippingAddress.postalCode,
                      country_code: order.details.shippingAddress.countryCode
                    }
                  }
                };

                if (order.details.billingAddress) {
                  request.transaction.billing_address_attributes = {
                    first_name: order.details.firstName,
                    last_name: order.details.lastName,
                    address_line_1: order.details.billingAddress.line1,
                    address_line_2: order.details.billingAddress.line2,
                    city: order.details.billingAddress.city,
                    state_code: order.details.billingAddress.state,
                    zip: order.details.billingAddress.postalCode,
                    country_code: order.details.billingAddress.countryCode
                  };
                }

                await createBraintreeTransaction({ body: request, order_token: props.cart.token })
                  .then((cart) => {
                    const cartData = cart?.data ?? cart;

                    if (cartData.errors && Array.isArray(cartData.errors)) {
                      cartData.errors.forEach((error) => {
                        if (error.message && typeof error.message === 'string') {
                          toast(error.message, { type: toast.TYPE.ERROR, autoClose: false });
                        }

                        if (error.code === 422) {
                          props.updateCart({
                            state: 'address'
                          });
                        }
                      });
                    } else {
                      props.updateCart({
                        ...cartData,
                        state: 'payment',
                        ...(props.isCart && { direct_checkout: true })
                      });

                      trackEvent({
                        eventCategory: 'Payment',
                        eventAction: 'Add PayPal payment',
                        eventLabel: 'Success'
                      });

                      Router.push({
                        pathname: '/checkout',
                        query: { paypal: 'true' }
                      }, '/checkout').then(() => {
                        if (global.window.location.pathname === '/checkout') {
                          trackEvent({
                            eventCategory: 'Cart',
                            eventAction: 'Submit cart',
                            eventLabel: 'Success'
                          });
                        }
                        global.window.scrollTo(0, 0);
                      });
                    }
                  });
              });
          },

          onCancel(data) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Cancelled'
            });

            console.error('checkout.js payment cancelled', JSON.stringify(data, 0, 2));
          },

          onError(err) {
            trackEvent({
              eventCategory: 'Payment',
              eventAction: 'Add PayPal payment',
              eventLabel: 'Failure'
            });

            Sentry.withScope((scope) => {
              scope.setContext('paypal instance', paypalCheckoutInstance);
              Sentry.captureException(err);
            });
          }
        }, `#${props.id}`);
      }
      return Promise.resolve();
    })
      .then(() => {
        setLoading(false);
      });
  }, [paypalLoaded]);

  return (
    <PayPalWrapper>
      <LoadPaypal setPaypalLoaded={setPaypalLoaded} />
      {loading && <Loading />}
      <PayPalOriginal id={props.id} />
      <PayPal type="hidden" isVisible={!loading}>
        <img src="data:image/svg+xml;base64,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" alt="PP" />
        <PPButtonSpace>&nbsp;</PPButtonSpace>
        <img src="data:image/svg+xml;base64,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" alt="PayPal" />
      </PayPal>
    </PayPalWrapper>
  );
};

BraintreePayPal.defaultProps = {
  isCart: false
};

BraintreePayPal.propTypes = {
  id: PropTypes.string.isRequired,
  cart: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  updateCart: PropTypes.func.isRequired,
  isCart: PropTypes.bool
};

const mapStateToProps = (state) => ({
  cart: state.cart
});

const mapDispatchToProps = (dispatch) => ({
  updateCart: (cart) => dispatch(updateCart(cart))
});

const ConnectedBraintreePayPal = connect(
  mapStateToProps, mapDispatchToProps
)(BraintreePayPal);

ConnectedBraintreePayPal.displayName = 'BraintreePayPal';
ConnectedBraintreePayPal.whyDidYouRender = true;

export default ConnectedBraintreePayPal;
